import { ChevronDoubleRightIcon, EyeIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import DOMPurify from "dompurify";
import React, { useEffect, useMemo, useRef, useState } from "react";

export type RetentionPreviewRow = {
  key: string;
  label: string;
  description: string;
  // The row of the step being edited right now; it is not in the policy until approved.
  isDraft: boolean;
};

// Inline formatting only: the policy prints the intro inside its own paragraph.
const inlineHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "u", "br"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });

// The "How long we keep your data" block of the privacy policy, built the same way the policy
// renders it (templates/privacy/03-california-privacy-notice-ccpa.ftl).
export function RetentionPolicyPreview({
  introHtml,
  isIntroDraft,
  rows,
  customHtml,
  onHide,
}: {
  introHtml: string;
  isIntroDraft?: boolean;
  rows: RetentionPreviewRow[];
  // "Add your own" mode prints the custom section instead of the intro and table.
  customHtml?: string;
  onHide: () => void;
}) {
  const intro = useMemo(() => inlineHtml(introHtml), [introHtml]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the row being edited in view as the user moves from step to step.
  const draftKey = rows.find((row) => row.isDraft)?.key;
  useEffect(() => {
    const container = scrollRef.current;
    const row = draftKey && container?.querySelector(`[data-row="${draftKey}"]`);
    if (container && row) {
      const offset = row.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollBy({ top: offset - 72, behavior: "smooth" });
    }
  }, [draftKey]);
  const custom = useMemo(
    () => (customHtml == null ? null : DOMPurify.sanitize(customHtml)),
    [customHtml],
  );

  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl border border-solid border-neutral-200 bg-neutral-0 shadow-[0px_1px_2px_0px_#1018280D]"
      style={{ maxHeight: "var(--retention-preview-max-height, calc(100vh - 170px))" }}
    >
      <div className="flex items-center justify-between gap-[12px] border-b border-solid border-neutral-200 bg-white px-[20px] py-[12px]">
        <div className="flex min-w-0 items-center gap-[8px]">
          <EyeIcon className="h-[18px] w-[18px] shrink-0 text-neutral-600" />
          <span className="text-[14px] font-semibold text-neutral-900">Preview</span>
          <span className="truncate text-[12px] text-neutral-500">Updates as you go</span>
        </div>
        <button
          type="button"
          aria-label="Hide preview"
          title="Hide preview"
          onClick={onHide}
          className="inline-flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-0 bg-transparent p-0 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
        >
          <ChevronDoubleRightIcon className="h-[18px] w-[18px]" />
        </button>
      </div>

      <div ref={scrollRef} className="overflow-y-auto p-[16px]">
        <div className="rounded-[8px] border border-solid border-neutral-200 bg-white px-[20px] py-[20px]">
          <div className="text-[12px] font-medium uppercase leading-[16px] tracking-[0.4px] text-neutral-500">
            Your privacy policy
          </div>
          <div className="mt-[8px] text-[16px] font-semibold leading-[24px] text-neutral-900">
            How long we keep your data
          </div>

          {custom != null ? (
            custom.replace(/<[^>]*>/g, "").trim() ? (
              <div
                className="mt-[8px] text-[13px] leading-[20px] text-neutral-700"
                dangerouslySetInnerHTML={{ __html: custom }}
              />
            ) : (
              <EmptyNote>Your retention policy text shows here as you write it.</EmptyNote>
            )
          ) : (
            <>
              <p
                className={clsx(
                  "mb-0 mt-[8px] rounded-[6px] text-[13px] leading-[20px] text-neutral-700 transition-colors duration-500",
                  isIntroDraft && "bg-primary-50 px-[8px] py-[6px]",
                )}
              >
                <span dangerouslySetInnerHTML={{ __html: intro }} />
                {rows.length > 0 && " We generally retain data according to the guidelines below."}
              </p>

              {rows.length > 0 ? (
                <table className="mt-[16px] w-full border-collapse text-left text-[12px] leading-[18px]">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="w-[40%] border border-solid border-neutral-200 bg-neutral-50 px-[10px] py-[8px] font-semibold text-neutral-800"
                      >
                        Type of Data
                      </th>
                      <th
                        scope="col"
                        className="border border-solid border-neutral-200 bg-neutral-50 px-[10px] py-[8px] font-semibold text-neutral-800"
                      >
                        Retention Period
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.key}
                        data-row={row.key}
                        className={clsx(
                          "align-top transition-colors duration-500",
                          row.isDraft ? "bg-primary-50" : "bg-white",
                        )}
                      >
                        <td className="border border-solid border-neutral-200 px-[10px] py-[8px] text-neutral-800">
                          {row.isDraft && (
                            <span className="mb-[4px] block text-[11px] font-semibold uppercase tracking-[0.4px] text-primary-600">
                              Editing
                            </span>
                          )}
                          {row.label}
                        </td>
                        <td className="border border-solid border-neutral-200 px-[10px] py-[8px] text-neutral-700">
                          {row.description || (
                            <span className="italic text-neutral-400">No retention period yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyNote>Your retention table builds here as you answer each question.</EmptyNote>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-[16px] rounded-[8px] border border-dashed border-neutral-300 px-[16px] py-[20px] text-center text-[13px] leading-[20px] text-neutral-500">
      {children}
    </div>
  );
}

// Sticky two-column frame: the steps on the left, the preview pinned on the right. The preview
// can be hidden; the choice is remembered per browser.
export function RetentionPolicyLayout({
  main,
  preview,
  isPreviewOpen,
  onShowPreview,
}: {
  main: React.ReactNode;
  preview: React.ReactNode;
  isPreviewOpen: boolean;
  onShowPreview: () => void;
}) {
  const stickyTop = 24 + useTopBannerHeight();
  return (
    // The row reaches into the page's bottom padding (the main column's padding, cancelled by the
    // row's negative margin, so the scroll length is unchanged). A sticky box can't leave its
    // parent's content box, so without this the preview runs out of room at the end of the page
    // and its top slides out of view.
    <div
      className="flex items-start justify-center gap-[40px] pr-[40px]"
      style={{ marginBottom: -256 }}
    >
      <div className="min-w-0 flex-1" style={{ maxWidth: 720, paddingBottom: 256 }}>
        {main}
      </div>
      {isPreviewOpen ? (
        <aside
          className="sticky shrink-0"
          style={
            {
              top: stickyTop,
              // Narrower on small laptops so the questions keep room for two columns.
              width: "clamp(300px, 26vw, 400px)",
              // Leaves room for the sticky page footer below the panel.
              "--retention-preview-max-height": `calc(100vh - ${stickyTop + 120}px)`,
            } as React.CSSProperties
          }
          aria-label="Privacy policy preview"
        >
          {preview}
        </aside>
      ) : (
        <div className="sticky shrink-0" style={{ top: stickyTop }}>
          <button
            type="button"
            onClick={onShowPreview}
            className="inline-flex cursor-pointer items-center gap-[8px] rounded-full border border-solid border-neutral-300 bg-white px-[14px] py-[8px] text-[14px] font-medium text-neutral-800 shadow-sm hover:border-primary-400 hover:text-primary-700"
          >
            <EyeIcon className="h-[18px] w-[18px]" />
            Show preview
          </button>
        </div>
      )}
    </div>
  );
}

// The app's notice banners (#top-banner) are fixed over the top of the page content, so sticky
// elements have to sit below them. Their alerts are absolutely positioned, so measure those.
export const useTopBannerHeight = () => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const mount = document.getElementById("top-banner");
    if (!mount) {
      return;
    }
    const measure = () => {
      const bottoms = Array.from(mount.querySelectorAll(":scope > *, :scope > * > *")).map(
        (el) => el.getBoundingClientRect().bottom,
      );
      setHeight(Math.max(0, ...bottoms));
    };
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(mount, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  return height;
};

const PREVIEW_OPEN_KEY = "polaris.retentionPreviewOpen";

export const usePreviewOpen = (): [boolean, (open: boolean) => void] => {
  const [isOpen, setIsOpen] = React.useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(PREVIEW_OPEN_KEY);
      if (stored != null) {
        return stored === "true";
      }
    } catch {
      // storage unavailable: fall through to the width default
    }
    return window.innerWidth >= 1360;
  });
  const set = (open: boolean) => {
    setIsOpen(open);
    try {
      window.localStorage.setItem(PREVIEW_OPEN_KEY, String(open));
    } catch {
      // not remembered, which is fine
    }
  };
  return [isOpen, set];
};
